import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Eye, EyeClosed, Mail, Lock, KeyRound, ArrowLeft, ArrowRight,
  RotateCcw, ShieldCheck, CircleCheck, CircleAlert,
} from "lucide-react";
import { BASE_URL } from "../../apiConfig";
import { useLanguage } from "../../context/LanguageProvider";

const maskEmail = (email) => {
  if (!email || !email.includes("@")) return email;
  const [name, domain] = email.split("@");
  const head = name.slice(0, Math.min(2, name.length));
  return `${head}${"*".repeat(Math.max(1, name.length - 2))}@${domain}`;
};

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();

  const [step, setStep] = useState(1); // 1 email | 2 code | 3 new password | 4 success
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const id = setInterval(() => setResendTimer((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [resendTimer]);

  useEffect(() => {
    if (step === 4) {
      const id = setTimeout(() => navigate("/login"), 2500);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [step, navigate]);

  const post = async (path, body) => {
    const response = await fetch(BASE_URL + path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    const firstError =
      data.errors && typeof data.errors === "object"
        ? Object.values(data.errors)[0]
        : null;
    let message = data.message || firstError || "";
    if (Array.isArray(message)) message = message[0];
    return { ok: data.status === 200, data, message };
  };

  const handleSendCode = async (e, resend = false) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { ok, message } = await post("/api/forgot-password", { email });
      if (ok) {
        if (!resend) setStep(2);
        setResendTimer(60);
      } else {
        setError(message || t("auth.server_error"));
      }
    } catch {
      setError(t("auth.server_error"));
    }
    setLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { ok, message } = await post("/api/verify-reset-code", { email, code });
      if (ok) {
        setStep(3);
      } else {
        setError(message || t("auth.reset_code_invalid"));
      }
    } catch {
      setError(t("auth.server_error"));
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      setError(t("auth.password_min_length"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("auth.password_mismatch"));
      return;
    }

    setError("");
    setLoading(true);
    try {
      const { ok, message } = await post("/api/reset-password", {
        email,
        code,
        password,
        password_confirmation: confirmPassword,
      });
      if (ok) {
        setStep(4);
      } else {
        setError(message || t("auth.reset_failed"));
      }
    } catch {
      setError(t("auth.server_error"));
    }
    setLoading(false);
  };

  const goBackToEmail = () => {
    setStep(1);
    setCode("");
    setPassword("");
    setConfirmPassword("");
    setError("");
  };

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="min-h-screen bg-gradient-to-br from-[var(--auth-bg-from)] via-[var(--auth-bg-via)] to-[var(--auth-bg-to)] flex items-center justify-center p-4 sm:p-8"
    >
      <div className="w-full max-w-md bg-gradient-to-br from-[var(--auth-card-from)] to-[var(--auth-card-to)] rounded-[2rem] shadow-2xl border border-[var(--color-primary-100)] px-8 py-10">
        <Link
          to="/login"
          className={`${isRTL ? "float-left" : "float-right"} inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[var(--color-primary-500)]`}
        >
          {isRTL ? (
            <ArrowRight className="w-4 h-4" />
          ) : (
            <ArrowLeft className="w-4 h-4" />
          )}
          {t("auth.reset_back_to_login")}
        </Link>

        <div className="clear-both">
          <span className="inline-flex items-center rounded-full bg-gradient-to-r from-[var(--color-primary-100)] to-[var(--color-primary-50)] border border-[var(--color-primary-200)] px-4 py-1.5 text-xs font-medium text-gray-700">
            {t("auth.my_platform")}
          </span>

          <div className="relative w-16 h-16 my-6">
            <div className="absolute inset-0 m-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--color-primary-500)] to-[var(--color-primary-300)] shadow-xl flex items-center justify-center">
              {step === 4 ? (
                <CircleCheck className="w-8 h-8 text-white" />
              ) : step === 2 ? (
                <ShieldCheck className="w-8 h-8 text-white" />
              ) : (
                <KeyRound className="w-8 h-8 text-white" />
              )}
            </div>
          </div>

          {step === 1 && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">
                {t("auth.reset_forgot_title")}
              </h1>
              <p className="text-gray-500 text-xs mb-6">
                {t("auth.reset_forgot_subtitle")}
              </p>

              <form onSubmit={handleSendCode} className="space-y-5">
                {error && (
                  <div className="text-red-600 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 px-4 py-2.5 rounded-xl text-xs">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-xs font-semibold text-gray-600 mb-2">
                    {t("auth.email_label")}
                  </label>
                  <div className="relative">
                    <Mail className={`${isRTL ? "right-4" : "left-4"} absolute top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4`} />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`${isRTL ? "pr-11" : "pl-11"} w-full text-xs py-3 rounded-2xl dark:text-white outline-none border-none bg-gray-100 focus:ring-2 focus:ring-[var(--color-primary-300)] transition-all duration-200 placeholder:text-gray-400`}
                      placeholder={t("auth.email_placeholder")}
                      required
                    />
                  </div>
                </div>

                <SubmitButton loading={loading} label={t("auth.reset_send_code")} />
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">
                {t("auth.reset_code_title")}
              </h1>
              <p className="text-gray-500 text-xs mb-6">
                {t("auth.reset_code_sent_to")} <strong className="text-gray-700">{maskEmail(email)}</strong>
              </p>

              <form onSubmit={handleVerifyCode} className="space-y-5">
                {error && (
                  <div className="text-red-600 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 px-4 py-2.5 rounded-xl text-xs">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="code" className="block text-xs font-semibold text-gray-600 mb-2">
                    {t("auth.reset_code_label")}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    id="code"
                    name="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="w-full h-14 text-center text-lg font-bold tracking-[0.6em] rounded-2xl dark:text-white outline-none border-none bg-gray-100 focus:ring-2 focus:ring-[var(--color-primary-300)] transition-all duration-200 placeholder:tracking-[0.6em] placeholder:text-gray-300 placeholder:text-lg"
                    placeholder="------"
                    maxLength={6}
                    required
                  />
                </div>

                <SubmitButton loading={loading} label={t("auth.reset_verify_button")} />

                <div className="flex items-center justify-between text-xs">
                  <button
                    type="button"
                    onClick={goBackToEmail}
                    className="text-gray-500 hover:text-[var(--color-primary-500)] underline underline-offset-2"
                  >
                    {t("auth.reset_change_email")}
                  </button>
                  <button
                    type="button"
                    disabled={resendTimer > 0 || loading}
                    onClick={(e) => handleSendCode(e, true)}
                    className={`inline-flex items-center gap-1 font-medium ${
                      resendTimer > 0
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-[var(--color-primary-500)] hover:text-[var(--color-primary-400)]"
                    }`}
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    {resendTimer > 0
                      ? `${t("auth.reset_resend_code")} (${resendTimer}s)`
                      : t("auth.reset_resend_code")}
                  </button>
                </div>
              </form>
            </>
          )}

          {step === 3 && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">
                {t("auth.reset_password_title")}
              </h1>
              <p className="text-gray-500 text-xs mb-6">
                {t("auth.reset_password_subtitle")}
              </p>

              <form onSubmit={handleResetPassword} className="space-y-5">
                {error && (
                  <div className="text-red-600 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 px-4 py-2.5 rounded-xl text-xs text-center">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="password" className="block text-xs font-semibold text-gray-600 mb-2">
                    {t("auth.password_label")}
                  </label>
                  <div className="relative">
                    <Lock className={`${isRTL ? "right-4" : "left-4"} absolute top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4`} />
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${isRTL ? "pr-11" : "pl-11"} w-full text-xs py-3 rounded-2xl dark:text-white outline-none border-none bg-gray-100 focus:ring-2 focus:ring-[var(--color-primary-300)] transition-all duration-200 placeholder:text-gray-400`}
                      placeholder={t("auth.password_placeholder")}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`${isRTL ? "left-4" : "right-4"} absolute cursor-pointer text-xs top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600`}
                      aria-label={showPassword ? t("auth.hide_password") : t("auth.show_password")}
                    >
                      {showPassword ? <Eye className="w-4 h-4" /> : <EyeClosed className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="password_confirmation" className="block text-xs font-semibold text-gray-600 mb-2">
                    {t("auth.confirm_password_label")}
                  </label>
                  <div className="relative">
                    <Lock className={`${isRTL ? "right-4" : "left-4"} absolute top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4`} />
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password_confirmation"
                      name="password_confirmation"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`${isRTL ? "pr-11" : "pl-11"} w-full text-xs py-3 rounded-2xl dark:text-white outline-none border-none bg-gray-100 focus:ring-2 focus:ring-[var(--color-primary-300)] transition-all duration-200 placeholder:text-gray-400`}
                      placeholder={t("auth.confirm_password_placeholder")}
                      required
                    />
                  </div>
                </div>

                <SubmitButton loading={loading} label={t("auth.reset_change_password_button")} />
              </form>
            </>
          )}

          {step === 4 && (
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-2 tracking-tight">
                {t("auth.reset_success_title")}
              </h1>
              <p className="text-gray-500 text-xs mb-6">
                {t("auth.reset_success_desc")}
              </p>
              <div className="inline-flex items-center gap-2 text-xs text-green-600 bg-gradient-to-r from-green-50 to-green-100 border border-green-200 px-4 py-2.5 rounded-xl">
                <CircleAlert className="w-4 h-4" />
                {t("auth.reset_redirecting")}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SubmitButton({ loading, label }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className={`w-full text-xs bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] cursor-pointer text-white py-3.5 px-4 rounded-2xl font-semibold shadow-lg transition-all duration-200 ${
        loading
          ? "opacity-60 cursor-not-allowed"
          : "hover:from-[var(--color-primary-400)] hover:to-[var(--color-primary-500)]"
      }`}
    >
      {loading ? (
        <svg
          className="animate-spin h-4 w-4 text-white mx-auto"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
          ></path>
        </svg>
      ) : (
        label
      )}
    </button>
  );
}
